import { lazy, Suspense, useEffect, useMemo, useState, ViewTransition } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router'
import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useMuatSebutanUnit } from '../store/muatSebutanUnit'
import { useSalin } from '../store/salin'
import { useSebutanUnit } from '../store/sebutanUnit'
import { cacheNamaParoki, catatanHeader, labelPeran, namaHeader, useSesi } from '../store/sesi'
import { useUi } from '../store/ui'
import { BottomBar } from './BottomBar'
import { Breadcrumbs } from './Breadcrumbs'
import { crumbsFromMatches } from './crumbs'
import { Header } from './Header'
import { LompatIsi } from './LompatIsi'
import { bottomBarUntukSesi, navAkunForRole, navUntukSesi } from './nav'
import { OfflineBand } from './OfflineBand'
import { PenjagaPerubahan } from './PenjagaPerubahan'
import { PilihTempatKerja } from './PilihTempatKerja'
import { PindahHalaman } from './PindahHalaman'
import { PitaPasang } from './PitaPasang'
import { PitaPasangUlang, PitaPindahAlamat } from './PitaPindahAlamat'
import { Sidebar, useSidebarOpen } from './Sidebar'
import { TabLain } from './TabLain'
import { UpdatePrompt } from './UpdatePrompt'

// The palette (cmdk with Radix Dialog) loads on its first opening, not with every launch.
const SearchPalette = lazy(() =>
  import('./SearchPalette').then((m) => ({ default: m.SearchPalette })),
)

function useDocumentTitle(): void {
  const crumbs = crumbsFromMatches(useMatches())
  const last = crumbs.at(-1)?.label
  useEffect(() => {
    document.title = last === undefined || last === 'Beranda' ? 'SATU' : `${last} · SATU`
  }, [last])
}

function useShortcuts(onCari: () => void, onSidebar: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return
      const key = event.key.toLowerCase()
      if (key === 'k') {
        event.preventDefault()
        onCari()
      } else if (key === 'b') {
        event.preventDefault()
        onSidebar()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCari, onSidebar])
}

/**
 * Peta umat (release 20): families with an address and no location are looked up in the
 * background, a parish's writer of families signed in and online. Its own chunk, after the
 * launch; it stops with the shell.
 */
function useCariLokasiLatar(): void {
  useEffect(() => {
    let batal = false
    let berhenti: (() => void) | null = null
    import('@/peta/latar')
      .then((m) => {
        if (!batal) berhenti = m.mulaiCariLokasiLatar()
      })
      .catch(() => {
        // The chunk failed to load (a deploy replaced it mid-session): the next launch starts it.
      })
    return () => {
      batal = true
      berhenti?.()
    }
  }, [])
}

/** Section 10's Meja Bersih: the chrome steps aside on a route whose handle says `salin`. */
function useMejaBersih(): boolean {
  const mejaBersih = useSalin((s) => s.mejaBersih)
  const matches = useMatches()
  return (
    mejaBersih && matches.some((m) => (m.handle as { salin?: boolean } | undefined)?.salin === true)
  )
}

/**
 * Rak Buku, the frame every screen sits in: skip link, header, role-filtered sidebar or
 * phone bar, the offline and update bands, and the outlet inside the one React
 * <ViewTransition> that owns route crossfades. When another tab holds the database the
 * second-tab screen replaces all of it. In register entry mode's focus mode (Meja
 * Bersih) the header, sidebar, bands, breadcrumbs and phone bar are left out and the
 * route fills the viewport; the outlet keeps its place in the tree so the form survives.
 */
export function AppShell() {
  useCariLokasiLatar()
  // Sebutan unit terkecil: every parish's word on this device, for every screen of the shell.
  useMuatSebutanUnit()
  const sesi = useSesi((s) => s.sesi)
  // Plan 10: the person (the panel's name and role, who may switch) and the parish they work in.
  const asli = useSesi((s) => s.asli)
  const catatan = useSesi(catatanHeader)
  const sebutanOrang = useSebutanUnit(asli.parokiId)
  const diParoki = catatan !== null
  const lock = useUi((s) => s.lock)
  const setCariOpen = useUi((s) => s.setCariOpen)
  const cariOpen = useUi((s) => s.cariOpen)
  const [cariPernahDibuka, setCariPernahDibuka] = useState(false)
  if (cariOpen && !cariPernahDibuka) setCariPernahDibuka(true)
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen()
  const groups = useMemo(() => navUntukSesi(sesi.peran, diParoki), [sesi.peran, diParoki])
  const bar = useMemo(() => bottomBarUntukSesi(sesi.peran, diParoki), [sesi.peran, diParoki])
  const akun = useMemo(() => navAkunForRole(sesi.peran), [sesi.peran])
  const bolehPilih = asli.peran === 'admin_keuskupan'
  // The palette still finds Akun and Pengaturan, which left the shelf's groups (plan 9b).
  const cari = useMemo(() => [...groups, { label: null, items: akun }], [groups, akun])
  const fokus = useMejaBersih()
  // Release 33: the install band on Beranda, the screen every sign-in opens (./PitaPasang).
  const diBeranda = useLocation().pathname === '/'

  useDocumentTitle()
  useEffect(() => {
    cacheNamaParoki(sesi)
  }, [sesi])
  useShortcuts(
    () => setCariOpen(true),
    () => setSidebarOpen(!sidebarOpen),
  )

  if (
    lock.status === 'dipegang_tab_lain' ||
    lock.status === 'meminta' ||
    lock.status === 'dipindahkan'
  ) {
    return <TabLain state={lock} />
  }

  return (
    <TooltipProvider>
      <LompatIsi />
      <div className="flex min-h-dvh flex-col">
        {fokus ? null : (
          <Header
            nama={namaHeader(sesi)}
            namaPendek={namaHeader(sesi, true)}
            catatan={catatan}
            onCari={() => setCariOpen(true)}
          />
        )}
        <div className="flex flex-1">
          {fokus ? null : (
            <Sidebar
              groups={groups}
              akun={akun}
              orang={{ nama: asli.nama, peran: labelPeran(asli.peran, sebutanOrang) }}
              {...(bolehPilih
                ? { atas: (ringkas: boolean) => <PilihTempatKerja ringkas={ringkas} /> }
                : {})}
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Release 26: the move to the new address (old address), the reinstall note (new). */}
            {fokus ? null : <PitaPindahAlamat />}
            {fokus ? null : <PitaPasangUlang />}
            {/* Release 33: install SATU on this device, once; it yields to the two above. */}
            {fokus || !diBeranda ? null : <PitaPasang />}
            {fokus ? null : <OfflineBand />}
            {fokus ? null : <UpdatePrompt />}
            <main
              id="isi"
              tabIndex={-1}
              className={cn(
                'mx-auto w-full flex-1 px-4 outline-none md:px-6 lg:px-8',
                fokus
                  ? 'max-w-none'
                  : 'max-w-[80rem] pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0',
              )}
            >
              <ViewTransition default="rute">
                {/* Plan 10: a switch of parish remounts every screen (none keeps another's data). */}
                <div key={sesi.parokiId ?? 'keuskupan'}>
                  {fokus ? null : <Breadcrumbs />}
                  <Outlet />
                </div>
              </ViewTransition>
            </main>
          </div>
        </div>
        {fokus ? null : (
          <BottomBar bar={bar} {...(bolehPilih ? { atas: <PilihTempatKerja /> } : {})} />
        )}
      </div>
      {cariPernahDibuka ? (
        <Suspense fallback={null}>
          <SearchPalette groups={cari} />
        </Suspense>
      ) : null}
      {/* Mounted whatever Meja Bersih hides: the one question before leaving unsaved work. */}
      <PenjagaPerubahan />
      {/* Another page: the focus to its heading, a handed-over sentence read out. */}
      <PindahHalaman />
    </TooltipProvider>
  )
}
